/**
 * AccountSettingsPage — Account Information & Setting page.
 *
 * Shows account info (name, login, active agent, role) and two tabs:
 * - Memory: the user's persistent memory records (DAT-8558 ListMemory),
 *   scoped by agent + memory type (LTM / episodic / reflection).
 * - Knowledge: the knowledge bases an agent can search (KnowledgeTab), read-only.
 *
 * A Capabilities tab (MCP services) is hidden until it has real data behind it.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAgent } from '../../context/AgentContext';
import { listMemory } from '../../client/SkillClient';
import type { MemoryRecord, MemoryType } from '../../client/SkillClient';
import KnowledgeTab from './KnowledgeTab';
import { SETTINGS_AGENTS } from './settingsAgents';
import './AccountSettingsPage.css';

const TABS = [
  { id: 'memory', labelKey: 'account.tabMemory' },
  { id: 'knowledge', labelKey: 'account.tabKnowledge' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function AccountSettingsPage() {
  const { t } = useTranslation('settings');
  const { uid, display_name } = useAuth();
  const navigate = useNavigate();
  const { currentAgentInfo } = useAgent();
  const [activeTab, setActiveTab] = useState<TabId>('memory');

  return (
    <div className="account-settings">
      <h1 className="account-settings__title">{t('account.title')}</h1>

      {/* Account Information card */}
      <section className="account-settings__info-card">
        <h2 className="account-settings__info-heading">{t('account.infoHeading')}</h2>
        <div className="account-settings__info-grid">
          <div className="account-settings__info-field">
            <span className="account-settings__info-label">{t('account.name')}</span>
            <span className="account-settings__info-value">{display_name || uid}</span>
          </div>
          <div className="account-settings__info-field">
            <span className="account-settings__info-label">{t('account.login')}</span>
            <span className="account-settings__info-value">{uid}</span>
          </div>
          <div className="account-settings__info-field">
            <span className="account-settings__info-label">{t('account.activeAgent')}</span>
            <span className="account-settings__info-value">
              <span className="account-settings__agent-badge">{currentAgentInfo.shortLabel}</span>
              {currentAgentInfo.label}
            </span>
          </div>
          <div className="account-settings__info-field">
            <span className="account-settings__info-label">{t('account.role')}</span>
            <span className="account-settings__info-value">
              {t('account.admin')}
              <a
                href="/admin-rbms"
                className="account-settings__edit-link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/admin-rbms');
                }}
              >
                {t('account.editInBackend')}
              </a>
            </span>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <nav className="account-settings__tabs" role="tablist" aria-label={t('account.tabsLabel')}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            id={`tab-${tab.id}`}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            className={`account-settings__tab${activeTab === tab.id ? ' account-settings__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
        <div className="account-settings__tab-indicator" />
      </nav>

      {/* Tab content */}
      <div
        className="account-settings__tab-content"
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        {activeTab === 'memory' && <MemoryTab />}
        {activeTab === 'knowledge' && <KnowledgeTab />}
      </div>
    </div>
  );
}


/**
 * Memory tab — lists the user's persistent memory records via the CMHKAgentPlatform
 * ListMemory API (DAT-8558). Scoped by agent (required) + memory type; LTM supports
 * an optional category filter. `all` merges the stores (no pagination); the single
 * stores support cursor pagination via nextToken ("Load more").
 */

// Agents the memory can be scoped to: ListMemory requires an agent_name (LTM filters
// by agent_name metadata; episodic/reflection key the actor as {user_alias}_{agent_name}).
const MEMORY_AGENTS = SETTINGS_AGENTS;

const MEMORY_TYPES = [
  { id: 'all', labelKey: 'memory.typeAll' },
  { id: 'ltm', labelKey: 'memory.typeLtm' },
  { id: 'episodic', labelKey: 'memory.typeEpisodic' },
  { id: 'reflection', labelKey: 'memory.typeReflection' },
] as const satisfies readonly { id: MemoryType; labelKey: string }[];

// '' = all categories. LTM only; ignored for episodic / reflection.
const LTM_CATEGORIES = ['', 'preference', 'fact', 'tool_usage'];

const MEMORY_PAGE_SIZE = 50;

const LOAD_FAILED = Symbol('loadFailed');

function formatMemoryDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function MemoryTab() {
  const { t } = useTranslation('settings');
  const { uid } = useAuth();
  const [agent, setAgent] = useState('rocky');
  const [memoryType, setMemoryType] = useState<MemoryType>('all');
  const [category, setCategory] = useState('');
  const [records, setRecords] = useState<MemoryRecord[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  // A thrown message is shown as-is; LOAD_FAILED is translated at render, so a locale
  // change neither shows stale text nor re-runs the load.
  const [error, setError] = useState<string | typeof LOAD_FAILED | null>(null);

  // Category filter only applies to the LTM portion.
  const showCategory = memoryType === 'all' || memoryType === 'ltm';

  // Staleness guard: scope changes (effect) and the Refresh / Load-more buttons
  // all call load(), so overlapping fetches can resolve out of order. Tag each
  // request and ignore any whose response is no longer the newest, so a slow
  // earlier request can't overwrite a fresher one's records/error.
  const reqIdRef = useRef(0);

  const load = useCallback(
    async (append = false, token?: string) => {
      if (!uid) return;
      const reqId = ++reqIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const { items, nextToken: nt } = await listMemory({
          userAlias: uid,
          agentName: agent,
          memoryType,
          category: showCategory && category ? category : undefined,
          maxResults: MEMORY_PAGE_SIZE,
          nextToken: token,
        });
        if (reqId !== reqIdRef.current) return; // superseded by a newer request
        setRecords((prev) => (append ? [...prev, ...items] : items));
        setNextToken(nt);
      } catch (e) {
        if (reqId !== reqIdRef.current) return; // superseded by a newer request
        setError(e instanceof Error ? e.message : LOAD_FAILED);
        if (!append) setRecords([]);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    },
    [uid, agent, memoryType, category, showCategory],
  );

  // Reload whenever the scope changes (load identity captures agent/type/category).
  useEffect(() => {
    load();
  }, [load]);

  const initialLoading = loading && records.length === 0;

  return (
    <div className="memory-tab">
      <div className="memory-tab__controls">
        <label className="memory-tab__control">
          <span className="memory-tab__control-label">{t('memory.agent')}</span>
          <select className="memory-tab__select" value={agent} onChange={(e) => setAgent(e.target.value)}>
            {MEMORY_AGENTS.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        </label>

        <label className="memory-tab__control">
          <span className="memory-tab__control-label">{t('memory.type')}</span>
          <select
            className="memory-tab__select"
            value={memoryType}
            onChange={(e) => setMemoryType(e.target.value as MemoryType)}
          >
            {MEMORY_TYPES.map((mt) => (
              <option key={mt.id} value={mt.id}>
                {t(mt.labelKey)}
              </option>
            ))}
          </select>
        </label>

        {showCategory && (
          <label className="memory-tab__control">
            <span className="memory-tab__control-label">{t('memory.category')}</span>
            <select className="memory-tab__select" value={category} onChange={(e) => setCategory(e.target.value)}>
              {LTM_CATEGORIES.map((c) => (
                <option key={c || 'all'} value={c}>
                  {c === '' ? t('memory.categoryAll') : c}
                </option>
              ))}
            </select>
          </label>
        )}

        <button className="memory-tab__refresh" onClick={() => load()} disabled={loading}>
          {loading ? t('memory.loadingShort') : t('memory.refresh')}
        </button>
      </div>

      {error && <div className="memory-tab__error">⚠ {error === LOAD_FAILED ? t('memory.loadFailed') : error}</div>}

      {initialLoading && <div className="memory-tab__loading">{t('memory.loading')}</div>}

      {!error && !initialLoading && records.length === 0 && (
        <div className="memory-tab__placeholder">
          <p>{t('memory.empty')}</p>
        </div>
      )}

      {records.length > 0 && (
        <ul className="memory-tab__list">
          {records.map((r) => (
            <li key={r.recordId} className="memory-tab__card">
              <div className="memory-tab__card-head">
                {r.category && (
                  <span className={`memory-tab__badge memory-tab__badge--${r.category}`}>{r.category}</span>
                )}
                {r.agentName && <span className="memory-tab__badge memory-tab__badge--agent">{r.agentName}</span>}
                {r.createdAt && <span className="memory-tab__date">{formatMemoryDate(r.createdAt)}</span>}
              </div>
              <div className="memory-tab__content">{r.content || <em>{t('memory.noContent')}</em>}</div>
            </li>
          ))}
        </ul>
      )}

      {memoryType !== 'all' && nextToken && (
        <button className="memory-tab__load-more" onClick={() => load(true, nextToken)} disabled={loading}>
          {loading ? t('memory.loadingShort') : t('memory.loadMore')}
        </button>
      )}
    </div>
  );
}
